import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    PageTitle,
    Loading,
    Button,
    DeviceSerialQrCodeModal
} from '../root/components/common'
import { useLocation, withRouter } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import * as actions from './actions'
import { enqueueSnackbar } from '../root/actions'
import { DevicesTable } from '../devices/components/devices-table'
import { isEmpty } from 'lodash'
import classNames from 'classnames'
import { useDevicesFilters } from '../root/components/common/device-filters/use-device-filters'
import {
    urlToSearchState,
    useSyncUrlState
} from 'web/src/utils/hooks/useSyncUrlState'
import { DeviceFilters } from '../root/components/common/device-filters/device-filters'
import { useReportTypes } from '../root/hooks/useReportTypes'
import { prepareFilters } from '../root/components/common/device-filters/utils'
import { UserPermissions } from '../../services/permissions'
import { downloadFile } from 'web/src/utils'
import { LOCAL_LOCALE_KEY } from '../root/constants'
import { getFileForExport } from '../devices/api/device-api'
import { composeFileName } from '../../utils'
import { ExportMenu } from '../root/components/common/export-menu'
import { debounce } from 'lodash'
import { useDevicesMultiselect } from '../root/hooks/use-devices-multiselect'
import { DevicesMultiselectMenu } from '../devices/components/devices-multiselect-menu'
import { IconButton } from '@material-ui/core'
import { X } from 'lucide-react'
import { MoveToLocationModal } from '../devices/move-to-location-modal'
import { DeleteDevicesModal } from '../devices/delete-devices-modal'
import { SwitchOffDevicesModal } from '../devices/switch-off-devices-modal'
import { SwitchOnMModeModal } from '../devices/switch-on-m-mode-modal'
import { shouldWarnAboutRetainedLocationSettingsOverrideForMoves } from './utils/device-location-settings-override'
import { LocationSettingsOverrideMoveConfirmationModal } from './components/device-location-settings-override-move-confirmation'

const useStyles = makeStyles(theme => ({
    switcher: {
        marginRight: 12,
        marginBottom: 12
    },
    wrapper: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    nameLink: {
        color: theme.textPrimary,
        textDecoration: 'none',
        fontWeight: 'bold',
        cursor: 'pointer'
    },
    actions: {
        display: 'flex',
        justifyContent: 'center'
    },
    icon: {
        color: theme.primary,
        cursor: 'pointer'
    },
    batteryWrapper: {
        display: 'flex',
        justifyContent: 'center'
    },
    statusWrapper: {
        display: 'flex',
        justifyContent: 'center'
    },
    modalButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 15
    },
    modalButton: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 15
    },
    pickerWrapper: {
        marginTop: 15
    },
    pageTitle: {
        justifyContent: 'space-between'
    },
    filtersWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap'
    },
    deviceFilters: {
        marginBottom: 12
    },
    exportBtnsWrapper: {
        display: 'flex',
        gap: '6px',
        marginRight: '16px'
    },
    loader: {
        top: '50vh',
        zIndex: 9,
        position: 'absolute',
        left: '20%',
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    disabledWrapper: {
        opacity: 0.3,
        pointerEvents: 'none'
    }
}))

function LocationDevices(props) {
    const classes = useStyles()
    const locationId = props.locationId || props.match.params.locationId
    const isNew = locationId === 'new'

    const {
        isDevicesToMoveMode,
        setIsDevicesToMoveMode,
        noTitle,
        className,
        embeded,
        noFilters
    } = props

    const isLoading = useSelector(state => state.main.isLoading)
    const locale = useSelector(state => state.main.locale)
    const theme = useSelector(state => state.main.theme)
    const isLoadingMoreDevices = useSelector(
        state => state.locations.isLoadingMoreDevices
    )
    const locationDevices = useSelector(
        state => state.locations.locationDevices
    )
    const location = useSelector(state => state.locations.location)
    const [currentDeviceSerial, setCurrentDeviceSerial] = useState(null)
    const [isOpenQrModal, setIsOpenQrModal] = useState(false)
    const [pendingDevicesToMove, setPendingDevicesToMove] = useState(null)
    const browserLocation = useLocation()
    const [filters, d] = useDevicesFilters(
        urlToSearchState(browserLocation),
        UserPermissions.isAllOriginalGranted()
    )
    const canUpdate =
        (location?.is_assigned &&
            UserPermissions.location(location?.customer_id).update) ||
        (UserPermissions.isAllOriginalGranted() &&
            UserPermissions.isOriginalEntity(location?.customer_id))

    useSyncUrlState(filters)
    const { reportSeverityGroups } = useReportTypes()
    const [isExporting, setIsExporting] = useState(false)

    const preparedFilters = useMemo(() => {
        return prepareFilters(
            filters,
            reportSeverityGroups,
            UserPermissions.isAllOriginalGranted()
        )
    }, [filters, reportSeverityGroups])

    const preparedMultiselectFilters = useMemo(() => {
        if (locationId !== 'new') {
            return {
                ...preparedFilters,
                locationId,
                with_children: 1
            }
        }
        return preparedFilters
    }, [preparedFilters, locationId])

    const {
        isRowSelected,
        selectRow,
        selectedRowsAmount,
        clearSelection,
        isMovingToLocationModalOpen,
        setIsMovingToLocationModalOpen,
        isDeleteDevicesModalOpen,
        setIsDeleteDevicesModalOpen,
        isSwitchOffModalOpen,
        setIsSwitchOffModalOpen,
        selectedDevicesIds,
        excludedDevicesIds,
        toggleAllSelector,
        isAllSelectorOn,
        isAllCheckboxIndeterminate,
        isMModeDevicesModalOpen,
        setIsMModeDevicesModalOpen
    } = useDevicesMultiselect(preparedFilters, locationId)

    /*
    const [devicesToShare, setDevicesToShare] = useState([])
    const [isShareModalOpen, setIsShareModalOpen] = useState(false)
    const [userToShareWith, setUserToShareWith] = useState(null)

    const sharedUsersOptions = sharedUsers
        ? sharedUsers.reduce((acc, user) => {
            acc[user.id] = user.name
            return acc
        }, {})
        : {} */

    const dispatch = useDispatch()

    /* const onSingleShareClick = (deviceId) => {
        setDevicesToShare([deviceId])
        setIsShareModalOpen(true)
    } */

    const moveDevices = devices => {
        dispatch(
            actions.moveDevicesRequest(
                devices.map(d => ({
                    id: Number(d.id),
                    location_id: Number(locationId),
                    customer_id: Number(d.customer_id)
                }))
            )
        )
        setIsDevicesToMoveMode(false)
    }

    const onSaveMove = async devices => {
        const devicesToMove = devices.filter(d => d.is_fresh)
        if (devicesToMove.length === 0) {
            dispatch(
                enqueueSnackbar({
                    message: locale.locations.noDevicesAdded,
                    options: {
                        variant: 'info'
                    }
                })
            )
            return
        }

        try {
            const shouldWarn =
                await shouldWarnAboutRetainedLocationSettingsOverrideForMoves([
                    { devices: devicesToMove, locationId }
                ])
            if (shouldWarn) {
                setPendingDevicesToMove(devicesToMove)
                return
            }
        } catch {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.commonLoadingError,
                    options: {
                        variant: 'error'
                    }
                })
            )
            return
        }

        moveDevices(devicesToMove)
    }

    const onAddDevicesClick = (locationId, customerId) => {
        dispatch(actions.availableDevicesRequest(locationId, customerId))
        setIsDevicesToMoveMode(true)
    }

    const onQrIconClick = deviceSerial => {
        setCurrentDeviceSerial(deviceSerial)
        setIsOpenQrModal(true)
    }

    /* const share = (userId, deviceIds) => {
        const devices = deviceIds.map(id => ({
            id: Number(id),
            user_id: Number(userId)
        }))
        dispatch(actions.shareDevicesRequest(devices))
        setIsShareModalOpen(false)
    } */

    const onRestoreDevice = deviceId => {
        dispatch(actions.moveFromLocationDevices(deviceId))
    }

    const exportDevices = async fileType => {
        setIsExporting(true)
        const locationName = location.name
        const locale_key = localStorage.getItem(LOCAL_LOCALE_KEY)
        const params = {
            ...preparedFilters,
            fileType,
            locale_key,
            locationId,
            locationName,
            with_children: isDevicesToMoveMode ? 0 : 1
        }
        const blob = await getFileForExport(params)
        if (blob) {
            const fileName = composeFileName('devices', fileType)
            downloadFile(blob, fileType, fileName)
        } else {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.getDevicesEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
        setIsExporting(false)
    }

    const initRequestLocationDevices = (
        locationId,
        isDevicesToMoveMode,
        preparedFilters
    ) => {
        dispatch(
            actions.locationDevicesRequest(
                locationId,
                isDevicesToMoveMode,
                preparedFilters
            )
        )
    }

    const initFetchMoreLocationDevices = (
        locationId,
        isDevicesToMoveMode,
        preparedFilters
    ) => {
        dispatch(
            actions.fetchMoreLocationDevices(
                locationId,
                isDevicesToMoveMode,
                preparedFilters
            )
        )
    }

    const reloadDevices = useCallback(() => {
        initRequestLocationDevices(
            locationId,
            isDevicesToMoveMode,
            preparedFilters
        )
        dispatch(actions.locationsRequest())
    }, [locationId, isDevicesToMoveMode, preparedFilters])

    const loadMoreLocationDevices = useCallback(
        debounce(() => {
            initFetchMoreLocationDevices(
                locationId,
                isDevicesToMoveMode,
                preparedFilters
            )
        }, 300),
        [preparedFilters, locationId, isDevicesToMoveMode]
    )

    useEffect(() => {
        if (!isNew && locationId)
            dispatch(
                actions.locationDevicesRequest(
                    locationId,
                    isDevicesToMoveMode,
                    preparedFilters
                )
            )
    }, [locationId, isDevicesToMoveMode, preparedFilters])

    useEffect(() => {
        if (!isNew && locationId) dispatch(actions.locationRequest(locationId))
        dispatch(actions.locationDevicesSuccess(null))
        clearSelection()
    }, [locationId])

    useEffect(() => {
        return () => {
            dispatch(actions.locationDevicesSuccess(null))
        }
    }, [])

    return (
        <>
            {isExporting && (
                <div className={classes.loader}>
                    <Loading />
                </div>
            )}
            <div
                className={classNames(classes.wrapper, className, {
                    [`${classes.disabledWrapper}`]: isExporting
                })}>
                {!noTitle ? (
                    <PageTitle
                        title={locale.locations.devicesOnLocation}
                        className={classes.pageTitle}>
                        {selectedRowsAmount ? (
                            <div
                                style={{
                                    height: 60,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                <DevicesMultiselectMenu
                                    selectedRowsAmount={selectedRowsAmount}
                                    setIsMovingToLocationModalOpen={
                                        setIsMovingToLocationModalOpen
                                    }
                                    setIsDeleteDevicesModalOpen={
                                        setIsDeleteDevicesModalOpen
                                    }
                                    setIsSwitchOffModalOpen={
                                        setIsSwitchOffModalOpen
                                    }
                                    setIsMModeDevicesModalOpen={
                                        setIsMModeDevicesModalOpen
                                    }
                                />
                                <IconButton
                                    onClick={clearSelection}
                                    style={{ marginLeft: 12 }}
                                    size="small">
                                    <X color={theme.primary} size={18} />
                                </IconButton>
                            </div>
                        ) : (
                            <>
                                <div className={classes.exportBtnsWrapper}>
                                    <ExportMenu>
                                        {({ handleClose }) => (
                                            <>
                                                <ExportMenu.Csv
                                                    locale={locale}
                                                    onExport={fileType => {
                                                        exportDevices(fileType)
                                                        handleClose()
                                                    }}
                                                />
                                                <ExportMenu.Excel
                                                    locale={locale}
                                                    onExport={fileType => {
                                                        exportDevices(fileType)
                                                        handleClose()
                                                    }}
                                                />
                                                <ExportMenu.Pdf
                                                    locale={locale}
                                                    onExport={fileType => {
                                                        exportDevices(fileType)
                                                        handleClose()
                                                    }}
                                                />
                                            </>
                                        )}
                                    </ExportMenu>
                                </div>
                                {isDevicesToMoveMode && canUpdate && (
                                    <Button
                                        onClick={onSaveMove.bind(
                                            this,
                                            locationDevices
                                        )}>
                                        {locale.common.save}
                                    </Button>
                                )}
                                {!isDevicesToMoveMode && canUpdate && (
                                    <Button
                                        onClick={onAddDevicesClick.bind(
                                            this,
                                            locationId,
                                            location.customer_id
                                        )}>
                                        {locale.locations.addDevices}
                                    </Button>
                                )}
                            </>
                        )}
                    </PageTitle>
                ) : null}
                {!noFilters
                    ? !isDevicesToMoveMode && (
                          <div className={classes.filtersWrapper}>
                              <DeviceFilters
                                  className={classes.deviceFilters}
                                  onChange={d}
                                  locale={locale}
                                  filters={filters}
                                  location={location}
                              />
                          </div>
                      )
                    : null}

                {!isLoading && isEmpty(locationDevices) && (
                    <div>{locale.locations.noDevicesSetupYet}</div>
                )}

                {!isEmpty(locationDevices) && (
                    <div style={{ flex: 1 }}>
                        <DevicesTable
                            embeded={embeded}
                            isLoading={isLoading}
                            isLoadingMore={isLoadingMoreDevices}
                            onLoadMore={loadMoreLocationDevices}
                            sorting={filters.sort_by}
                            onSort={sort_by => d({ type: 'sort', sort_by })}
                            devices={locationDevices}
                            onQrIconClick={onQrIconClick}
                            isDevicesToMoveMode={isDevicesToMoveMode}
                            onRestoreDevice={onRestoreDevice}
                            multiselect={{
                                isRowSelected,
                                selectRow,
                                toggleAllSelector,
                                isAllSelectorOn,
                                isAllCheckboxIndeterminate
                            }}
                        />
                    </div>
                )}

                <DeviceSerialQrCodeModal
                    deviceSerial={currentDeviceSerial}
                    open={isOpenQrModal}
                    setOpen={setIsOpenQrModal}
                />
            </div>

            <MoveToLocationModal
                open={isMovingToLocationModalOpen}
                onClose={() => setIsMovingToLocationModalOpen(false)}
                selectedDevicesIds={selectedDevicesIds}
                excludedDevicesIds={excludedDevicesIds}
                filters={preparedMultiselectFilters}
                clearSelection={clearSelection}
                reloadDevices={reloadDevices}
            />

            <DeleteDevicesModal
                open={isDeleteDevicesModalOpen}
                onClose={() => setIsDeleteDevicesModalOpen(false)}
                selectedDevicesIds={selectedDevicesIds}
                excludedDevicesIds={excludedDevicesIds}
                filters={preparedMultiselectFilters}
                clearSelection={clearSelection}
                reloadDevices={reloadDevices}
            />

            <SwitchOffDevicesModal
                open={isSwitchOffModalOpen}
                onClose={() => setIsSwitchOffModalOpen(false)}
                selectedDevicesIds={selectedDevicesIds}
                excludedDevicesIds={excludedDevicesIds}
                filters={preparedMultiselectFilters}
                clearSelection={clearSelection}
                reloadDevices={reloadDevices}
            />

            <SwitchOnMModeModal
                open={isMModeDevicesModalOpen}
                onClose={() => setIsMModeDevicesModalOpen(false)}
                selectedDevicesIds={selectedDevicesIds}
                excludedDevicesIds={excludedDevicesIds}
                filters={preparedMultiselectFilters}
                clearSelection={clearSelection}
                reloadDevices={reloadDevices}
            />

            <LocationSettingsOverrideMoveConfirmationModal
                open={Boolean(pendingDevicesToMove)}
                onConfirm={() => {
                    moveDevices(pendingDevicesToMove)
                    setPendingDevicesToMove(null)
                }}
                onCancel={() => setPendingDevicesToMove(null)}
                locale={locale}
            />
        </>
    )
}

export default withRouter(LocationDevices)
